import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { MButton } from "@/components/ui/atoms/MButtons";
import AutoCompleteInput from "@/components/ui/organisms/AutoCompleteInput";
import { MInputField } from "@/components/ui/atoms/MInputField";
import MDropDownDrawer from "@/components/ui/molecules/MDropDownDrawer";
import { useMedicines } from "@/hooks/useMedicine";
import { DayFrequency, ReminderFrequency } from "@/types/reminders";
import { useMemberMedications } from "@/hooks/member-medication.hook";
import { useMemberSupplements } from "@/hooks/member-supplement.hook";
import { memberMedicationService } from "@/services/member-medication.service";
import { memberSupplementService } from "@/services/member-supplement.service";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import { IMedication } from "@/types/medication";
import { IconPill, IconPillFilled, IconLeaf, IconPlus, IconChevronLeft, IconChevronRight, IconX } from "@tabler/icons-react";
import { getMemberInfo } from "@/hooks/useMembers";
import FormHeader from "@/components/ui/organisms/MHeader";
import MSpacer from "@/components/ui/atoms/MSpacer";
import { useTranslation } from "react-i18next";
import { DAYS_OF_WEEK } from "@/utils/constants";
import { paintDayFrequencyField } from "@/pages/reminders/remindersHelper";
import { trackUserDataLoaded } from "@/utils/gtm";
import { ANALYTICS_SCREEN_TYPES, ANALYTICS_SCREEN_NAMES } from "@/constants/analytics.constants";
import styles from "./MedicationForm.module.css";

type MedicationType = 'medicine' | 'supplement';

interface MedicationData {
  name: string;
  dosage: string;
  frequency?: ReminderFrequency;
  selectedWeekdays?: string[];
  everyXDays?: string;
  dayFrequency?: DayFrequency | null;
}

interface FormData {
  currentType: MedicationType;
  medicineData: MedicationData;
  supplementData: MedicationData;
  saved: boolean; // Track if this form has been saved to backend
  savedType?: MedicationType; // Track which type was saved (medicine or supplement)
}

const emptyMedicationData: MedicationData = {
  name: '',
  dosage: '',
  frequency: undefined,
  selectedWeekdays: [],
  everyXDays: '',
  dayFrequency: null
};

const MedicationForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const mode = params.get("mode") as "add" | "edit";
  const editIndex = params.get("index")
    ? Number(params.get("index"))
    : undefined;
  const editType = params.get("type") as "medicine" | "supplement" | null;
  const memberInfo = getMemberInfo();
  const memberId = memberInfo.id;
  const { showToast } = useToast();
  
  // Get initial type from URL parameter to prevent flicker
  const initialType = (editType === 'supplement' || editType === 'medicine') ? editType : 'medicine';
  
  // Multi-form state - each form has separate data for medicine and supplement
  const [forms, setForms] = useState<FormData[]>([
    {
      currentType: initialType,
      medicineData: { ...emptyMedicationData },
      supplementData: { ...emptyMedicationData },
      saved: false,
      savedType: undefined
    }
  ]);
  const [currentFormIndex, setCurrentFormIndex] = useState(0);
  const [showFrequencyDrawer, setShowFrequencyDrawer] = useState(false);
  const frequencyDrawerOptions = [
    {
      value: ReminderFrequency.EVERY_DAY,
      label: t("reminders.frequency.EVERY_DAY"),
    },
    {
      value: ReminderFrequency.EVERY_OTHER_DAY,
      label: t("reminders.frequency.EVERY_OTHER_DAY"),
    },
    {
      value: ReminderFrequency.SPECIFIC_DAYS,
      label: t("reminders.frequency.SPECIFIC_DAYS"),
    },
    {
      value: ReminderFrequency.DAY_FREQUENCY,
      label: t("reminders.frequency.DAY_FREQUENCY"),
    },
  ];
  const weekdaysOptions = Object.keys(DAYS_OF_WEEK).map((key) => ({
    value: key,
    label: t(`reminders.daysOfWeek.${key}`),
  }));

  const [showWeekdaysDrawer, setShowWeekdaysDrawer] = useState(false);
  const [showDayFrequencyDrawer, setShowDayFrequencyDrawer] =
    useState<boolean>(false);

  const handleFrequencyDrawerSelect = (value: string) => {
    if (value === ReminderFrequency.SPECIFIC_DAYS) {
      setCurrentFormData({ selectedWeekdays: [] });
    }
    setCurrentFormData({ frequency: value as ReminderFrequency });
    setShowFrequencyDrawer(false);
  };

  const handleWeekdaysDone = (selected: string[]) => {
    setCurrentFormData({ selectedWeekdays: selected });
    setShowWeekdaysDrawer(false);
  };

  const [savingMedications, setSavingMedications] = useState(false);
  const [editingBackendMed, setEditingBackendMed] =
    useState<IMedication | null>(null);
  const [editingBackendSupplement, setEditingBackendSupplement] =
    useState<any | null>(null);
  
  // Current form and current type data shortcuts
  const currentForm = forms[currentFormIndex];
  const currentTypeData = currentForm.currentType === 'medicine' 
    ? currentForm.medicineData 
    : currentForm.supplementData;
  
  const setCurrentFormData = (updates: Partial<MedicationData>) => {
    setForms(prev => {
      const newForms = [...prev];
      const form = newForms[currentFormIndex];
      if (form.currentType === 'medicine') {
        form.medicineData = { ...form.medicineData, ...updates };
      } else {
        form.supplementData = { ...form.supplementData, ...updates };
      }
      return newForms;
    });
  };
  
  const setCurrentType = (type: MedicationType) => {
    setForms(prev => {
      const newForms = [...prev];
      newForms[currentFormIndex].currentType = type;
      return newForms;
    });
  };

  // Set currentType based on savedType when navigating to a saved form
  useEffect(() => {
    if (currentForm.saved && currentForm.savedType) {
      setCurrentType(currentForm.savedType);
    }
  }, [currentFormIndex, currentForm.saved, currentForm.savedType]);

  // Helper function to navigate with tab parameter
  const navigateWithTab = (tabType: 'medicine' | 'supplement', isEditMode: boolean = false) => {
    // For edit mode, always go to medications listing with tab
    if (isEditMode) {
      navigate(`/profile/health-profile/medication?tab=${tabType}`);
      return;
    }

    navigate(`/profile/health-profile/medication?tab=${tabType}`);
  };

  const { searchMedicines, searchResults, searchLoading, searchHasMore, loadMoreSearchResults } = useMedicines();

  // Use member medications and supplements hooks
  const { medications, fetchMedications } = useMemberMedications(memberId);
  const { supplements, fetchSupplements } = useMemberSupplements(memberId);
  useEffect(() => {
    if (mode === "edit" && editType === "medicine") fetchMedications();
    if (mode === "edit" && editType === "supplement") fetchSupplements();
  }, [memberId, mode, editType, fetchMedications, fetchSupplements]);

  useEffect(() => {
    if (mode === "add") {
      // Use type from URL parameter to set initial currentType
      const initialType = (editType === 'supplement' || editType === 'medicine') ? editType : 'medicine';
      setForms([{
        currentType: initialType,
        medicineData: { ...emptyMedicationData },
        supplementData: { ...emptyMedicationData },
        saved: false,
        savedType: undefined
      }]);
      setCurrentFormIndex(0);
      setEditingBackendMed(null);
      setEditingBackendSupplement(null);
    } else if (mode === "edit" && editIndex !== undefined) {
      if (editType === "medicine" && medications) {
        const med = medications[editIndex];
        if (!med) return;
        setEditingBackendMed(med);
        setForms([{
          currentType: 'medicine',
          medicineData: {
            name: med.name,
            dosage: med.dosage || '',
            frequency: med.frequency || undefined,
            selectedWeekdays: med.selectedWeekdays ?? [],
            everyXDays: med.everyXDays ?? '0',
            dayFrequency: med.dayFrequency ?? null
          },
          supplementData: { ...emptyMedicationData },
          saved: false,
          savedType: undefined
        }]);
        setCurrentFormIndex(0);
      } else if (editType === "supplement" && supplements) {
        const supp = supplements[editIndex];
        if (!supp) return;
        setEditingBackendSupplement(supp);
        setForms([{
          currentType: 'supplement',
          medicineData: { ...emptyMedicationData },
          supplementData: {
            name: supp.name,
            dosage: supp.dosage || '',
            frequency: supp.frequency || undefined,
            selectedWeekdays: supp.selectedWeekdays ?? [],
            everyXDays: supp.everyXDays ?? '0',
            dayFrequency: supp.dayFrequency ?? null
          },
          saved: false,
          savedType: undefined
        }]);
        setCurrentFormIndex(0);
      }
    }
  }, [mode, editIndex, editType, medications, supplements]);

  // Track page load for add mode only
  useEffect(() => {
    if (mode === "add") {
      trackUserDataLoaded({
        memberInfo,
        screenType: ANALYTICS_SCREEN_TYPES.PROFILE,
        screenName: ANALYTICS_SCREEN_NAMES.MEDICINE_ADD,
      });
    }
  }, [mode]);

  const handleSave = async () => {
    // Validate all forms before saving
    if (!checkisValid()) {
      return;
    }
    
    setSavingMedications(true);
    try {
      // Track completion for add mode only
      if (mode === "add") {
        trackUserDataLoaded({
          memberInfo,
          screenType: ANALYTICS_SCREEN_TYPES.PROFILE,
          screenName: ANALYTICS_SCREEN_NAMES.MEDICINE_ADD_COMPLETE,
        });
      }
      
      if (mode === "edit" && editingBackendMed) {
        const medId = editingBackendMed.id;
        const formData = currentForm.medicineData;
        try {
          await memberMedicationService.updateForMember(memberId, medId, {
            ...editingBackendMed,
            name: formData.name.trim(),
            dosage: formData.dosage,
            frequency: formData.frequency,
            everyXDays: formData.everyXDays,
            selectedWeekdays: formData.selectedWeekdays,
            dayFrequency: formData.dayFrequency,
          });
        } catch (e) {
          console.error('Error updating medicine:', e);
        }
        
        // Navigate to appropriate tab after edit
        navigateWithTab(editType || 'medicine', true);
      } else if (mode === "edit" && editingBackendSupplement) {
        const suppId = editingBackendSupplement.id;
        const formData = currentForm.supplementData;
        await memberSupplementService.updateForMember(memberId, suppId, {
          ...editingBackendSupplement,
          name: formData.name.trim(),
          dosage: formData.dosage,
          frequency: formData.frequency,
          everyXDays: formData.everyXDays,
          selectedWeekdays: formData.selectedWeekdays,
          dayFrequency: formData.dayFrequency,
        });
        
        // Navigate to appropriate tab after edit
        navigateWithTab(editType || 'medicine', true);
      } else {
        // Save only the CURRENT form (not all unsaved forms)
        const medicines: any[] = [];
        const supplements: any[] = [];
        
        // Only process the current form being viewed
        const form = currentForm;

        // Skip if already saved
        if (form.saved) {
          navigateWithTab(form.savedType || 'medicine');
          return;
        }
        
        if (form.medicineData.name.trim()) {
          medicines.push({
            name: form.medicineData.name.trim(),
            dosage: form.medicineData.dosage,
            frequency: form.medicineData.frequency,
            everyXDays: form.medicineData.everyXDays,
            selectedWeekdays: form.medicineData.selectedWeekdays,
            dayFrequency: form.medicineData.dayFrequency,
          });
        }
        if (form.supplementData.name.trim()) {
          supplements.push({
            name: form.supplementData.name.trim(),
            dosage: form.supplementData.dosage,
            frequency: form.supplementData.frequency,
            everyXDays: form.supplementData.everyXDays,
            selectedWeekdays: form.supplementData.selectedWeekdays,
            dayFrequency: form.supplementData.dayFrequency,
          });
        }
        
        if (medicines.length > 0) {
          await memberMedicationService.createForMember(memberId, medicines);
        }
        if (supplements.length > 0) {
          await memberSupplementService.createForMember(memberId, supplements);
        }
        
        // Determine which tab to show based on what was saved
        const savedType = supplements.length > 0 ? 'supplement' : 'medicine';
        
        // Navigate to appropriate tab based on what was saved
        navigateWithTab(savedType);
      }
    } catch (e) {
      showToast({
        message: t("errors.toast"),
        variant: "error",
      });
    } finally {
      setSavingMedications(false);
    }
  };

  const getOptionLabel = (med: any) => med.medicine_name;

  const renderOption = (med: any) => (
    <div>
      <div className="font-medium text-gray-900">{med.medicine_name}</div>
      {med.dosage && <div className="text-xs text-gray-500">{med.dosage}</div>}
    </div>
  );

  const handleMedicineSelect = (item: any) => {
    if (typeof item === "string") {
      setCurrentFormData({ name: item });
    } else {
      setCurrentFormData({ 
        name: item.medicine_name,
        dosage: item.dosage 
      });
    }
  };

  const handleMedicineChange = (value: string) => {
    setCurrentFormData({ name: value });
  };

  const displayMedicineFrequency = (): string | undefined => {
    if (!currentTypeData.frequency) return undefined;
    return t(`reminders.frequency.${currentTypeData.frequency}`);
  };

  const isCurrentFormValid = (): boolean => {
    if (savingMedications) return false;
    if (!currentTypeData.name?.trim()) return false;
    if (!currentTypeData.frequency) return false;
    if (!currentTypeData.dayFrequency) return false;

    switch (currentTypeData.frequency) {
      case ReminderFrequency.SPECIFIC_DAYS:
        return (currentTypeData.selectedWeekdays?.length ?? 0) > 0;
      case ReminderFrequency.DAY_FREQUENCY:
        return Number(currentTypeData.everyXDays) > 0;
      default:
        return true;
    }
  };

  const checkisValid = (): boolean => {
    // Check if all UNSAVED forms have at least one valid type (medicine or supplement)
    if (savingMedications) return false;
    
    return forms.every(form => {
      // Skip validation for already saved forms
      if (form.saved) return true;
      
      // In edit mode, only check the relevant data type
      if (mode === "edit") {
        if (editType === "medicine") {
          return form.medicineData.name?.trim() && 
            form.medicineData.frequency && 
            form.medicineData.dayFrequency &&
            (form.medicineData.frequency !== ReminderFrequency.SPECIFIC_DAYS || (form.medicineData.selectedWeekdays?.length ?? 0) > 0) &&
            (form.medicineData.frequency !== ReminderFrequency.DAY_FREQUENCY || Number(form.medicineData.everyXDays) > 0);
        } else if (editType === "supplement") {
          return form.supplementData.name?.trim() && 
            form.supplementData.frequency && 
            form.supplementData.dayFrequency &&
            (form.supplementData.frequency !== ReminderFrequency.SPECIFIC_DAYS || (form.supplementData.selectedWeekdays?.length ?? 0) > 0) &&
            (form.supplementData.frequency !== ReminderFrequency.DAY_FREQUENCY || Number(form.supplementData.everyXDays) > 0);
        }
      }
      
      // In add mode, check if either medicine or supplement is valid
      const medicineValid = form.medicineData.name?.trim() && 
        form.medicineData.frequency && 
        form.medicineData.dayFrequency &&
        (form.medicineData.frequency !== ReminderFrequency.SPECIFIC_DAYS || (form.medicineData.selectedWeekdays?.length ?? 0) > 0) &&
        (form.medicineData.frequency !== ReminderFrequency.DAY_FREQUENCY || Number(form.medicineData.everyXDays) > 0);
      
      const supplementValid = form.supplementData.name?.trim() && 
        form.supplementData.frequency && 
        form.supplementData.dayFrequency &&
        (form.supplementData.frequency !== ReminderFrequency.SPECIFIC_DAYS || (form.supplementData.selectedWeekdays?.length ?? 0) > 0) &&
        (form.supplementData.frequency !== ReminderFrequency.DAY_FREQUENCY || Number(form.supplementData.everyXDays) > 0);
      
      return medicineValid || supplementValid;
    });
  };

  const displaySelectedWeekdays = (): string | undefined => {
    if (!currentTypeData.selectedWeekdays?.length) return "";
    return currentTypeData.selectedWeekdays
      .map((day) => t(`reminders.daysOfWeek.${day}`))
      .join(", ");
  };
  
  const handleAddAnother = async () => {
    // Check if current form has at least one valid type (medicine or supplement)
    const form = currentForm;
    const medicineValid = form.medicineData.name?.trim() && 
      form.medicineData.frequency && 
      form.medicineData.dayFrequency &&
      (form.medicineData.frequency !== ReminderFrequency.SPECIFIC_DAYS || (form.medicineData.selectedWeekdays?.length ?? 0) > 0) &&
      (form.medicineData.frequency !== ReminderFrequency.DAY_FREQUENCY || Number(form.medicineData.everyXDays) > 0);
    
    const supplementValid = form.supplementData.name?.trim() && 
      form.supplementData.frequency && 
      form.supplementData.dayFrequency &&
      (form.supplementData.frequency !== ReminderFrequency.SPECIFIC_DAYS || (form.supplementData.selectedWeekdays?.length ?? 0) > 0) &&
      (form.supplementData.frequency !== ReminderFrequency.DAY_FREQUENCY || Number(form.supplementData.everyXDays) > 0);
    
    if (!medicineValid && !supplementValid) {
      return;
    }
    
    // Save current form data to backend before adding new form
    setSavingMedications(true);
    try {
      const medicines: any[] = [];
      const supplements: any[] = [];
      
      if (form.medicineData.name.trim()) {
        medicines.push({
          name: form.medicineData.name.trim(),
          dosage: form.medicineData.dosage,
          frequency: form.medicineData.frequency,
          everyXDays: form.medicineData.everyXDays,
          selectedWeekdays: form.medicineData.selectedWeekdays,
          dayFrequency: form.medicineData.dayFrequency,
        });
      }
      if (form.supplementData.name.trim()) {
        supplements.push({
          name: form.supplementData.name.trim(),
          dosage: form.supplementData.dosage,
          frequency: form.supplementData.frequency,
          everyXDays: form.supplementData.everyXDays,
          selectedWeekdays: form.supplementData.selectedWeekdays,
          dayFrequency: form.supplementData.dayFrequency,
        });
      }
      
      if (medicines.length > 0) {
        await memberMedicationService.createForMember(memberId, medicines);
      }
      if (supplements.length > 0) {
        await memberSupplementService.createForMember(memberId, supplements);
      }
      
      // Mark current form as saved and preserve the saved data
      setForms(prev => {
        const newForms = [...prev];
        const savedType = form.medicineData.name.trim() ? 'medicine' : 'supplement';
        newForms[currentFormIndex] = {
          ...form,
          saved: true,
          savedType: savedType
        };
        // Add new empty form with completely fresh data
        newForms.push({
          currentType: 'medicine',
          medicineData: {
            name: '',
            dosage: '',
            frequency: undefined,
            selectedWeekdays: [],
            everyXDays: '',
            dayFrequency: null
          },
          supplementData: {
            name: '',
            dosage: '',
            frequency: undefined,
            selectedWeekdays: [],
            everyXDays: '',
            dayFrequency: null
          },
          saved: false
        });
        return newForms;
      });
      setCurrentFormIndex(forms.length);
    } catch (e) {
      console.log(e);
    } finally {
      setSavingMedications(false);
    }
  };
  
  const handlePrevForm = () => {
    if (currentFormIndex > 0) {
      setCurrentFormIndex(currentFormIndex - 1);
    }
  };
  
  const handleNextForm = () => {
    // Validate current form before moving to next
    if (!isCurrentFormValid()) {
      return;
    }
    
    if (currentFormIndex < forms.length - 1) {
      setCurrentFormIndex(currentFormIndex + 1);
    }
  };
  
  const handleRemoveForm = () => {
    // Only allow removing unsaved forms that are not the first form
    if (currentForm.saved || currentFormIndex === 0) return;
    
    // If this is the only form or first form, navigate back to listing
    if (forms.length === 1 || currentFormIndex === 0) {
      navigate('/profile/health-profile/medication');
      return;
    }
    
    setForms(prev => prev.filter((_, index) => index !== currentFormIndex));
    
    // Navigate to previous form
    setCurrentFormIndex(currentFormIndex - 1);
  };

  return (
    <div className="profile-page-root">
      <FormHeader module="add_medications" memberInfo={memberInfo} />
      <MSpacer mt={32}/>
      <div className="profile-input-container">
        {/* Navigation controls - only show when multiple forms exist */}
        {forms.length > 1 && (
          <div className={styles.navigationContainer}>
            <MButton
              variant="icon-only-outline"
              size="md"
              leftIcon={<IconChevronLeft size={20} />}
              onClick={handlePrevForm}
              disabled={currentFormIndex === 0}
            />
            <div className={styles.counterDisplay}>
              {currentFormIndex + 1} of {forms.length}
            </div>
            <MButton
              variant="icon-only-outline"
              size="md"
              leftIcon={<IconChevronRight size={20} />}
              onClick={handleNextForm}
              disabled={currentFormIndex === forms.length - 1}
            />
          </div>
        )}

        {/* Form area with border - use different styling in edit mode */}
        <div className={mode === 'edit' ? styles.editModeFormContainer : styles.formContainer}>
          {/* Close icon - only show for additional unsaved forms (not first form) */}
          {currentFormIndex > 0 && !currentForm.saved && (
            <button
              onClick={handleRemoveForm}
              className={styles.closeIcon}
              type="button"
              aria-label="Remove form"
            >
              <IconX size={24} />
            </button>
          )}
          {/* Medicine/Supplement Toggle - hide in edit mode */}
          {mode === 'add' && (
            <div className={styles.toggleSection}>
              <label className={`profile-input-label ${styles.toggleLabel}`}>
                Choose the medication type
              </label>
              <div className={styles.toggleButtons}>
                <MButton
                  variant={currentForm.currentType === 'medicine' ? 'filled' : 'outline'}
                  size="md"
                  text="Medicine"
                  leftIcon={currentForm.currentType === 'medicine' ? <IconPillFilled size={20} /> : <IconPill size={20} />}
                  onClick={() => setCurrentType('medicine')}
                  disabled={currentForm.saved && currentForm.currentType !== 'medicine'}
                />
                <MButton
                  variant={currentForm.currentType === 'supplement' ? 'filled' : 'outline'}
                  size="md"
                  text="Supplement"
                  leftIcon={<IconLeaf size={20} />}
                  onClick={() => setCurrentType('supplement')}
                  disabled={currentForm.saved && currentForm.currentType !== 'supplement'}
                  style={{ width: '47%' }}
                />
              </div>
            </div>
          )}

          <AutoCompleteInput
            key={`autocomplete-${currentFormIndex}-${currentForm.currentType}`}
            label={currentForm.currentType === 'medicine' ? t("add_medications.medicineName") : t("add_medications.supplementName")}
            data={searchResults}
            getOptionLabel={getOptionLabel}
            renderOption={renderOption}
            onSelect={handleMedicineSelect}
            placeholder={t("common.enterOrChoose")}
            value={currentTypeData.name}
            onChange={handleMedicineChange}
            className="w-full"
            loading={searchLoading}
            onSearch={searchMedicines}
            minLength={3}
            onLoadMore={loadMoreSearchResults}
            hasMore={searchHasMore}
            readOnly={currentForm.saved}
          />
          <MInputField
            key={`frequency-${currentFormIndex}-${currentForm.currentType}`}
            label={t("add_medications.frequency")}
            placeholder={t("common.notAnswered")}
            value={displayMedicineFrequency()}
            readOnly
            onClick={currentForm.saved ? undefined : () => setShowFrequencyDrawer(true)}
            showIcon={!currentForm.saved}
            icon="caret-down-filled"
          />
          {currentTypeData.frequency === ReminderFrequency.DAY_FREQUENCY && (
            <div>
              <label className="profile-input-label">
                {t("add_medications.everyXDays.label")}
              </label>
              <MInputField
                key={`everyXDays-${currentFormIndex}-${currentForm.currentType}`}
                type="number"
                showLabel={false}
                value={currentTypeData.everyXDays}
                siUnit={t("add_medications.everyXDays.unit")}
                showSIUnit
                onChange={(e) => setCurrentFormData({ everyXDays: e.target.value })}
                className="w-[156.5px]"
                readOnly={currentForm.saved}
              />
            </div>
          )}
          {currentTypeData.frequency === ReminderFrequency.SPECIFIC_DAYS && (
            <MInputField
              key={`weekdays-${currentFormIndex}-${currentForm.currentType}`}
              label={t("add_medications.specificDays.label")}
              placeholder={t("common.notAnswered")}
              value={displaySelectedWeekdays()}
              onClick={currentForm.saved ? undefined : () => setShowWeekdaysDrawer(true)}
              showIcon={!currentForm.saved}
              icon="caret-down-filled"
            />
          )}
          <div key={`dayFrequency-${currentFormIndex}-${currentForm.currentType}`} style={{ width: '100%' }}>
            {paintDayFrequencyField(
              currentTypeData.dayFrequency,
              (value: DayFrequency | null) => setCurrentFormData({ dayFrequency: value }),
              showDayFrequencyDrawer,
              setShowDayFrequencyDrawer,
              t,
              "medication.healthProfile"
            )}
          </div>
        </div>

        {/* Add another button - only show in add mode */}
        {mode === 'add' && (
          <MButton
            variant="outline-dashed"
            size="lg"
            text="Add another (Optional)"
            leftIcon={<IconPlus size={20} />}
            onClick={handleAddAnother}
            className={styles.addAnotherButton}
          />
        )}
      </div>
      <MDropDownDrawer
        open={showFrequencyDrawer}
        onClose={() => setShowFrequencyDrawer(false)}
        title={t("add_medications.frequencySelect")}
        items={frequencyDrawerOptions}
        initialValue={currentTypeData.frequency}
        onSelect={handleFrequencyDrawerSelect}
        variant="single"
      />
      <MDropDownDrawer
        open={showWeekdaysDrawer}
        onClose={() => setShowWeekdaysDrawer(false)}
        title={t("add_medications.frequencySpecificDaysLabel")}
        items={weekdaysOptions}
        selectedValues={currentTypeData.selectedWeekdays}
        onSelect={() => {}}
        onDone={handleWeekdaysDone}
        variant="multi"
      />
      <div className="confirm-footer">
        <MButton
          type="button"
          variant="filled"
          size="lg"
          className="w-full h-[56px]"
          disabled={!checkisValid()}
          onClick={handleSave}
        >
          {savingMedications ? t("common.saving") : t("common.done")}
        </MButton>
      </div>
    </div>
  );
};

export default MedicationForm;
